/**
 * Game — R2.1 compact console layout.
 *
 * Fits everything in ONE viewport with NO scrolling:
 *   Top strip (44px) · model stage (flex-1) · thumb ribbon (44px)
 *   · bet chips (36px) · color chips (72px) · CTA (52px) · PF pill (32px)
 * = 280px fixed + flex stage → fits 375×667 (iPhone SE) through 1920×1080.
 *
 * Two-column layout activates only at viewports ≥ 1024w AND ≥ 900h.
 * Otherwise single-column stacked (mobile, tablet, short laptops).
 *
 * Single-click "OPEN DRESS · $X" behaviour preserved (openDress()).
 * Everything not essential to placing/revealing is tucked into drawers/modals:
 *   - Provably-fair panel  → bottom sheet on tap of the PF pill
 *   - Recent history       → modal
 *   - Rotate seeds         → modal (existing)
 *   - Demo top-up          → modal (existing)
 * No AppShell wrapper — /game runs standalone for maximum viewport.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ChevronDown, History as HistoryIcon, Lock, LogOut, Plus,
  RefreshCw, ShieldCheck, Sparkles, Trophy, Volume2, VolumeX, Sliders, X,
} from "lucide-react";
import { toast, Toaster } from "sonner";

import api, { asset } from "../lib/api";
import { extractError } from "../lib/formatError";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import ScratchGame from "./games/ScratchGame";
import {
  BET_CHIPS_BY_CURRENCY,
  CURRENCIES,
  MAX_BET_BY_CURRENCY,
  MIN_BET_BY_CURRENCY,
  formatAmount,
  getBalance,
} from "../lib/currency";
import Button from "../components/ui-r1/Button";
import Input from "../components/ui-r1/Input";
import { Badge } from "../components/ui-r1/Badge";
import ModeSegmented, { LegacyModeAliases } from "../components/ui-r1/ModeSegmented";
import ChatWidget from "../components/ChatWidget";
import soundManager from "../lib/soundManager";

// Visual metadata for every color the platform can render. Games pick from this
// pool via `state.config.colors` — Bra 3-Way is [red, black, gold], Bra 2-Way
// is [red, black]. Adding a new game color? Add its meta here.
const COLOR_META = {
  red:    { key: "red",    label: "RED",    hex: "#c73030", ring: "#ff5252" },
  black:  { key: "black",  label: "BLACK",  hex: "#141110", ring: "#7a7268" },
  gold:   { key: "gold",   label: "GOLD",   hex: "#d4a13a", ring: "#ffce55" },
};
const BANNER_COLOR = {
  red: "#ff6b6b", black: "#f4ede0", gold: "#ffce55",
  tattoo: "#FF3B7A", no_tattoo: "#f4ede0",
};
const DEFAULT_SLUG = "bra-3-way";

// Per-game copy for the primary reveal CTA. The bra games "open a dress",
// sexy-tattoo "reveals a tattoo" — all the same bet+reveal flow underneath,
// only the verb changes. Add new slugs here as they launch.
const CTA_VERB_BY_SLUG = {
  "bra-3-way": "Open dress",
  "bra-2-way": "Open dress",
  "sexy-tattoo": "Reveal tattoo",
  "hot-bikini": "Show Bikini",
};
const DEFAULT_CTA_VERB = "Open dress";

// Per-game display name used as a fallback until `gameMeta` from the backend
// arrives (rare — first paint before /games/{slug} resolves). Extends
// automatically for any new slug by falling back to a Title-Cased version
// of the slug.
const GAME_TITLE_FALLBACK = {
  "bra-3-way": "Bra 3-Way",
  "bra-2-way": "Bra 2-Way",
  "sexy-tattoo": "Sexy Tattoo",
  "hot-bikini": "Hot Bikini",
};

// Fallback display labels for text-style choice games. Overridden by
// `state.config.choice_labels` when the backend surfaces it.
const CHOICE_LABEL_FALLBACK = {
  tattoo: "TATTOO",
  no_tattoo: "NO TATTOO",
};

// Two-column layout activates only when BOTH conditions hold.
// IMPORTANT: Tailwind 3.4's JIT scanner is purely textual — it will NOT pick
// up arbitrary variants built via template interpolation (e.g. `${WIDE}:flex-row`).
// The full class strings below must appear as literals in this file for the
// content scanner to emit the CSS rules. Do not refactor into a constant.
//   [@media(min-width:1024px)_and_(min-height:900px)]:flex-row
//   [@media(min-width:1024px)_and_(min-height:900px)]:w-[62%]
//   [@media(min-width:1024px)_and_(min-height:900px)]:w-[38%]
//   [@media(min-width:1024px)_and_(min-height:900px)]:flex-none
//   [@media(min-width:1024px)_and_(min-height:900px)]:flex-1
//   [@media(min-width:1024px)_and_(min-height:900px)]:justify-between
//   [@media(min-width:1024px)_and_(min-height:900px)]:p-6
//   [@media(min-width:1024px)_and_(min-height:900px)]:gap-4

export default function Game() {
  const { user, refresh, logout } = useAuth();
  const { activeCurrency, setActiveCurrency } = useCurrency();
  const nav = useNavigate();
  const { slug: rawSlug } = useParams();
  const slug = rawSlug || DEFAULT_SLUG;
  const [gameMeta, setGameMeta] = useState(null);
  // True when the backend reports the slug doesn't exist / is inactive. Used
  // to short-circuit the render (redirect) instead of showing a partial UI
  // that falls back to the default game's styling.
  const [gameNotFound, setGameNotFound] = useState(false);
  // Detected via /games/{slug}.game_type — kept so future crash-style games
  // can be re-added without touching this file's routing scaffold. When a
  // crash game is present, `<CrashGame />` (deleted with Sexy Crash) would
  // render instead of the choice-and-reveal chrome below.
  // eslint-disable-next-line no-unused-vars
  const isCrashGame = gameMeta?.game_type === "crash";
  // Scratch-style games have their own canvas-based reveal UI — hand off
  // to `<ScratchGame />` (see pages/games/ScratchGame.jsx) instead of the
  // shared choice-and-reveal shell below.
  const isScratchGame = gameMeta?.game_type === "scratch";

  const [models, setModels] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [state, setState] = useState(null);
  const [colorPick, setColorPick] = useState("red");
  // Bet defaults to the currency's smallest chip. Reset whenever the active
  // currency flips so we never send a USDT-sized bet against a BDT balance.
  const [bet, setBet] = useState(() => BET_CHIPS_BY_CURRENCY[activeCurrency]?.[2] ?? 1);
  const [mode, setMode] = useState("demo");
  const [placing, setPlacing] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [pending, setPending] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNowTs] = useState(Date.now());
  const [showFair, setShowFair] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showRotate, setShowRotate] = useState(false);
  const [showTopup, setShowTopup] = useState(false);
  const [showCurrency, setShowCurrency] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const [soundState, setSoundState] = useState(() => soundManager.getState());
  const galleryIntervalRef = useRef(null);

  // Currency-aware bet bounds, refreshed whenever the active currency changes.
  const minBet = MIN_BET_BY_CURRENCY[activeCurrency] ?? 1;
  const maxBet = MAX_BET_BY_CURRENCY[activeCurrency] ?? 1000;
  const betChips = BET_CHIPS_BY_CURRENCY[activeCurrency] || [1, 5, 10, 50, 100];

  // Snap the bet to the currency's mid chip whenever the active currency
  // changes — otherwise a USDT bet of `1` bleeds into BDT (where 1 is legal
  // but no chip is highlighted, and the CTA reads ৳1.00 which is below the
  // smallest visible chip of 10).
  useEffect(() => {
    setBet(betChips[2] ?? minBet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCurrency]);

  const refreshAll = useCallback(async () => {
    try {
      // Fetch the game meta first so we can branch on `game_type` before
      // touching the legacy /state and /models routes, which are only wired
      // for choice-and-reveal games. Scratch games have their own state
      // machine + endpoints (see ScratchGame.jsx) — hitting /state on a
      // scratch slug currently 500s (backend `payout_multiplier` is null on
      // scratch registry docs). Bailing here keeps the render flow clean.
      const { data: meta } = await api.get(`/games/${slug}`);
      setGameMeta(meta);
      if (meta?.game_type === "scratch") {
        // ScratchGame will handle its own fetches. Nothing else to do here.
        return;
      }
      const [{ data: modelsPayload }, { data: stateData }] = await Promise.all([
        api.get(`/games/${slug}/models`),
        api.get(`/games/${slug}/state`),
      ]);
      const modelsList = modelsPayload.models || [];
      setModels(modelsList);
      setState(stateData);
      // Initial colorPick — use first color for this game (red by default for both games).
      const cols = (stateData?.config?.colors) || (meta?.colors) || ["red"];
      setColorPick((prev) => (cols.includes(prev) ? prev : cols[0]));
      if (stateData.has_pending_round && stateData.pending_round) {
        setPending(stateData.pending_round);
        const idx = modelsList.findIndex((m) => m.id === stateData.pending_round.model_id);
        if (idx >= 0) setSelectedIdx(idx);
        setColorPick(stateData.pending_round.color_pick);
        setBet(stateData.pending_round.bet_amount);
        setMode(stateData.pending_round.mode || "demo");
      } else {
        // Reset selection to first model when switching games (unless a pending round pins it).
        setSelectedIdx(0);
      }
    } catch (e) {
      // 404 on the parent /games/{slug} = the slug isn't in the registry
      // (e.g. game was retired). Flip the not-found flag so we render a
      // clean not-found screen and redirect to /casino, not the default
      // game's ghost UI.
      if (e?.response?.status === 404) {
        setGameNotFound(true);
      } else {
        setError(extractError(e));
      }
    }
  }, [slug]);
  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Redirect on not-found — small delay so the message flashes for a beat.
  useEffect(() => {
    if (!gameNotFound) return;
    const t = setTimeout(() => nav("/casino", { replace: true }), 2500);
    return () => clearTimeout(t);
  }, [gameNotFound, nav]);

  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => setNowTs(Date.now()), 500);
    return () => clearInterval(t);
  }, [pending]);

  useEffect(() => {
    if (models.length <= 1 || pending || reveal) return;
    galleryIntervalRef.current = setInterval(() => {
      setCountdown((c) => Math.max(0, c - 1));
    }, 1000);
    return () => clearInterval(galleryIntervalRef.current);
  }, [models.length, pending, reveal]);

  // When countdown hits 0 (idle), advance model and reset.
  useEffect(() => {
    if (countdown === 0 && !pending && !reveal && models.length > 1) {
      if (soundManager.isModelSwooshEnabled()) soundManager.play("model_swoosh");
      setSelectedIdx((i) => (i + 1) % models.length);
      setCountdown(10);
    }
  }, [countdown, pending, reveal, models.length]);

  // Sound: subscribe to soundManager for UI reactivity (mute icon, sliders).
  useEffect(() => soundManager.subscribe(setSoundState), []);

  // Sound: ambient loop while on /game (no-op if music disabled).
  useEffect(() => {
    soundManager.play("ambient_loop");
    return () => soundManager.stop("ambient_loop");
  }, []);

  // Sound: reveal_tick loop while mid-flight (placing/revealing, no reveal yet).
  useEffect(() => {
    if ((placing || revealing) && !reveal) soundManager.play("reveal_tick");
    else soundManager.stop("reveal_tick");
  }, [placing, revealing, reveal]);

  // Sound: reveal chime + win/lose + coin cascade when reveal resolves.
  useEffect(() => {
    if (!reveal) return;
    soundManager.play("reveal_chime");
    if (reveal.won) {
      const t1 = setTimeout(() => soundManager.play("win_celebration"), 300);
      const t2 = setTimeout(() => soundManager.play("coin_pile"), 300 + 800);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } else {
      const t = setTimeout(() => soundManager.play("lose_soft"), 300);
      return () => clearTimeout(t);
    }
  }, [reveal]);

  // Sound: countdown tick on the final 3 seconds (idle only).
  useEffect(() => {
    if (pending || reveal || placing || revealing) return;
    if (countdown === 1 || countdown === 2 || countdown === 3) {
      soundManager.play("countdown_tick");
    }
  }, [countdown, pending, reveal, placing, revealing]);

  const selected = models[selectedIdx];

  const secsLeft = useMemo(() => {
    if (!pending?.expires_at) return null;
    return Math.max(0, Math.round((new Date(pending.expires_at).getTime() - now) / 1000));
  }, [pending, now]);

  useEffect(() => {
    if (secsLeft === 0 && pending && !revealing) {
      toast.error("Round expired — balance refunded");
      setPending(null);
      setTimeout(refreshAll, 500);
    }
  }, [secsLeft, pending, revealing, refreshAll]);

  // Balance shown in the header/console — sourced natively from the user's
  // per-currency map for the active (mode, currency) pair. There is NO USD
  // conversion — the number displayed is the number wagered.
  const balance = getBalance(user, mode, activeCurrency);
  const realBalance = getBalance(user, "real", activeCurrency);
  const demoBalance = getBalance(user, "demo", activeCurrency);

  const canOpen =
    !!selected && !placing && !revealing && !pending &&
    Number(bet) >= minBet && Number(bet) <= maxBet && Number(bet) <= balance;

  const openDress = async () => {
    if (!selected || !canOpen) return;
    setError(null); setReveal(null); setPlacing(true);
    soundManager.play("spin_start");

    let roundId = null;
    let roundMode = mode;
    let roundCurrency = activeCurrency;
    try {
      const { data } = await api.post(`/games/${slug}/bet`, {
        model_id: selected.id,
        color_pick: colorPick,
        bet_amount: Number(bet),
        mode,
        currency: activeCurrency,
      });
      roundId = data.round_id;
      roundMode = mode;
      setPending({
        round_id: data.round_id,
        model_id: data.model_id,
        color_pick: data.color_pick,
        bet_amount: data.bet_amount,
        expires_at: data.expires_at,
        mode,
        currency: activeCurrency,
      });
      // Refresh user object so the multi-currency map reflects the debit.
      // (The backend returns `balance_after` for the specific bucket, but
      // refresh is the simplest way to keep every surface consistent.)
      refresh();
    } catch (e) {
      setError(extractError(e));
      setPlacing(false);
      return;
    }

    setRevealing(true);
    try {
      const { data } = await api.post(`/games/${slug}/reveal`, { round_id: roundId });
      setReveal({ ...data, mode: roundMode, currency: roundCurrency });
      setState((s) => (s ? { ...s, has_pending_round: false, nonce: data.nonce_used + 1 } : s));
      setPending(null);
      refresh();
      setTimeout(() => setReveal(null), 3000);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) {
        toast.error("Round expired — refunded automatically");
        setPending(null);
        setTimeout(refreshAll, 500);
      } else {
        setError("Reveal failed — bet will be refunded within 60 seconds");
      }
    } finally {
      setPlacing(false);
      setRevealing(false);
    }
  };

  const retryReveal = async () => {
    if (!pending) return;
    setError(null); setRevealing(true);
    try {
      const { data } = await api.post(`/games/${slug}/reveal`, { round_id: pending.round_id });
      setReveal({ ...data, mode: pending.mode, currency: pending.currency || activeCurrency });
      setState((s) => (s ? { ...s, has_pending_round: false, nonce: data.nonce_used + 1 } : s));
      setPending(null);
      refresh();
      setTimeout(() => setReveal(null), 3000);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) {
        toast.error("Round expired — refunded automatically");
        setPending(null);
        setTimeout(refreshAll, 500);
      } else {
        setError(extractError(e));
      }
    } finally {
      setRevealing(false);
    }
  };

  const activeImage = useMemo(() => {
    if (reveal) return asset(reveal.reveal_image_url);
    return selected ? asset(selected.dressed_url) : null;
  }, [reveal, selected]);

  const jumpToThumb = (i) => {
    if (pending) return;
    setSelectedIdx(i);
    setReveal(null);
    setCountdown(10);
  };

  // Not-found short-circuit — renders BEFORE the shared game shell so we
  // don't leak the default game's title/CTA/color-scheme into the frame
  // while the redirect timer fires.
  if (gameNotFound) {
    return (
      <div
        className="fixed inset-0 flex flex-col items-center justify-center bg-base text-ink px-6"
        data-testid="game-not-found"
      >
        <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-3">Game unavailable</div>
        <h1 className="font-display italic text-3xl sm:text-4xl mb-2 text-center" data-testid="game-not-found-title">
          This game is no longer available
        </h1>
        <p className="text-sm text-ink-2 mb-6 text-center max-w-md">
          The game <span className="font-mono text-ink">{slug}</span> has been retired or is
          temporarily offline. Redirecting you to the casino lobby…
        </p>
        <Link
          to="/casino"
          className="px-6 py-3 rounded-lg bg-brand-gradient text-white text-sm font-medium shadow-glow-pink hover:brightness-110 transition"
          data-testid="game-not-found-cta"
        >
          Back to casino
        </Link>
      </div>
    );
  }

  // Scratch-style games use a canvas foil-reveal UI — hand off before
  // rendering the choice-and-reveal chrome. Any future scratch game just
  // needs `game_type:"scratch"` on the registry doc; no per-slug branch.
  if (isScratchGame) {
    return <ScratchGame />;
  }

  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden bg-base text-ink"
      data-testid="game-page"
    >
      <Toaster theme="dark" position="top-center" />

      {/* ---------- Top strip (44px) ---------- */}
      <div className="h-11 shrink-0 flex items-center gap-2 px-2 sm:px-3 border-b border-line bg-elevated/70 backdrop-blur relative z-30">
        <button
          onClick={() => nav("/dashboard")}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-2 hover:text-ink hover:bg-highest transition"
          aria-label="Back to dashboard"
          data-testid="game-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>

        <Badge variant="hot" className="hidden sm:inline-flex" data-testid="game-title-badge">{gameMeta?.name || GAME_TITLE_FALLBACK[slug] || "Bra 3-Way"}</Badge>

        {/* Segmented REAL / DEMO mode toggle — replaces the old tiny mode pill
            + separate balance chip + real-balance link. Both wallets' balances
            are always visible; whichever is highlighted (pink→purple gradient)
            is the current wallet. `game-balance` and `game-real-balance` testid
            aliases are preserved below for legacy e2e tests. */}
        <div className="flex items-center gap-1.5" data-testid="game-mode-cluster">
          <ModeSegmented
            mode={mode}
            setMode={(m) => !pending && !placing && !revealing && setMode(m)}
            realBalance={realBalance}
            demoBalance={demoBalance}
            disabled={!!pending || placing || revealing}
            size="compact"
          />
          <button
            onClick={() => setShowTopup(true)}
            className="h-9 w-9 rounded-full border border-line hover:border-pink text-pink hover:bg-highest transition flex items-center justify-center shrink-0"
            aria-label="Top up demo balance"
            title="Top up demo balance"
            data-testid="topup-btn"
          >
            <Plus className="w-4 h-4" />
          </button>
          {/* Currency picker — persists via CurrencyContext (localStorage) so the
              same pick is reflected on the wallet + AppShell top bar. */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCurrency((s) => !s)}
              disabled={!!pending || placing || revealing}
              className={
                "h-9 px-2.5 rounded-lg border border-line hover:border-pink text-[10px] tracking-widest uppercase flex items-center gap-1.5 shrink-0 " +
                "text-ink hover:bg-highest transition font-mono " +
                ((!!pending || placing || revealing) ? "opacity-60 cursor-not-allowed" : "cursor-pointer")
              }
              aria-label="Change bet currency"
              data-testid="game-currency-btn"
            >
              <span className="text-gold">{activeCurrency}</span>
              <ChevronDown className="w-3 h-3 text-ink-3" />
            </button>
            {showCurrency && (
              <>
                <button
                  className="fixed inset-0 z-40"
                  aria-label="close"
                  onClick={() => setShowCurrency(false)}
                />
                <div
                  className="absolute right-0 top-full mt-1.5 w-56 z-50 rounded-lg border border-line bg-elevated shadow-card-lg p-1.5"
                  data-testid="game-currency-menu"
                >
                  <div className="px-2 py-1 text-[9px] tracking-[0.3em] uppercase text-ink-3">
                    Bet in
                  </div>
                  {CURRENCIES.map((c) => {
                    const bal = getBalance(user, mode, c.code);
                    const active = c.code === activeCurrency;
                    return (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => {
                          setActiveCurrency(c.code);
                          setShowCurrency(false);
                        }}
                        className={
                          "w-full flex items-center justify-between px-2 py-1.5 rounded-md text-xs transition " +
                          (active
                            ? "bg-pink/10 text-pink"
                            : "text-ink-2 hover:text-ink hover:bg-highest")
                        }
                        data-testid={`game-currency-option-${c.code}`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-ink-3 w-4 inline-flex justify-center">
                            {c.symbol}
                          </span>
                          <span className="uppercase tracking-widest text-[10px]">{c.code}</span>
                        </span>
                        <span className="font-mono text-[10px] text-ink-3">
                          {formatAmount(bal, c.code)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
        <LegacyModeAliases />
        {/* Legacy testid aliases (kept invisible for pre-existing e2e tests) */}
        <span className="hidden font-mono text-gold text-sm" data-testid="game-balance">{formatAmount(balance ?? 0, activeCurrency)}</span>
        <span className="hidden font-mono text-gold" data-testid="game-real-balance">{formatAmount(realBalance, activeCurrency)}</span>
        <Link to={`/wallet/deposit?currency=${activeCurrency}`} className="hidden" data-testid="game-deposit-link" aria-hidden="true" tabIndex={-1} />

        <div className="flex-1" />

        <button
          onClick={() => setShowHistory(true)}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-2 hover:text-pink hover:bg-highest transition"
          aria-label="History"
          data-testid="nav-history"
        >
          <HistoryIcon className="w-4 h-4" />
        </button>
        <Link
          to="/verify"
          className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-2 hover:text-gold hover:bg-highest transition"
          aria-label="Verifier"
          data-testid="nav-verify"
        >
          <ShieldCheck className="w-4 h-4" />
        </Link>
        <button
          onClick={() => soundManager.toggleMute()}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-2 hover:text-pink hover:bg-highest transition"
          aria-label={soundState.muted ? "Unmute" : "Mute"}
          title={soundState.muted ? "Sound muted" : "Sound on"}
          data-testid="sound-mute-btn"
        >
          {soundState.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <button
          onClick={() => setShowSoundSettings(true)}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-2 hover:text-gold hover:bg-highest transition"
          aria-label="Sound settings"
          title="Sound settings"
          data-testid="sound-settings-btn"
        >
          <Sliders className="w-4 h-4" />
        </button>
        <button
          onClick={async () => { await logout(); nav("/"); }}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-ink-2 hover:text-danger hover:bg-highest transition"
          aria-label="Log out"
          data-testid="game-logout-btn"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {/* ---------- Main — stacked mobile / two-col on wide+tall ---------- */}
      <div className="flex-1 min-h-0 flex flex-col [@media(min-width:1024px)_and_(min-height:900px)]:flex-row overflow-hidden">

        {/* Model stage — takes remaining vertical space (mobile) or 62% width (wide+tall) */}
        <section
          className="relative flex-1 min-h-0 min-w-0 overflow-hidden [@media(min-width:1024px)_and_(min-height:900px)]:w-[62%] [@media(min-width:1024px)_and_(min-height:900px)]:flex-none"
          data-testid="stage-frame"
        >
          {activeImage ? (
            <>
              {/* Ambient blurred background — fills stage on wide viewports */}
              <img
                key={`bg-${activeImage}`}
                aria-hidden="true"
                src={activeImage}
                className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-40 pointer-events-none"
                draggable="false"
              />
              <div className="absolute inset-0 bg-base/40 pointer-events-none" />

              {/* Sharp portrait — aspect-preserved, full model visible, centered */}
              <div className="absolute inset-0 flex items-center justify-center p-1 sm:p-2">
                <img
                  key={activeImage}
                  src={activeImage}
                  alt={selected?.prompt_summary || "model"}
                  className="max-w-full max-h-full w-auto h-auto object-contain crossfade drop-shadow-[0_10px_40px_rgba(0,0,0,0.5)]"
                  data-testid="stage-image"
                  draggable="false"
                />
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-ink-3 text-sm animate-pulse">
              Loading…
            </div>
          )}

          {/* Top-of-stage vignette (light) */}
          <div className="absolute inset-x-0 top-0 h-16 pointer-events-none bg-gradient-to-b from-base/50 to-transparent" />
          {/* Bottom-of-stage vignette (heavier — keeps caption + banner legible) */}
          <div className="absolute inset-x-0 bottom-0 h-28 pointer-events-none bg-gradient-to-t from-base/80 to-transparent" />

          {/* Model caption — pinned to the LETTERBOX area at very bottom (over the ambient blur, below the sharp image). Hidden during reveal. */}
          {selected && !reveal && (
            <div
              className="absolute inset-x-0 bottom-1 text-center text-[10px] tracking-[0.28em] uppercase text-ink-2/80 pointer-events-none z-[1] px-2 line-clamp-1"
              data-testid="model-caption"
            >
              {selected.id.replace("_", " · ")} · {selected.prompt_summary}
            </div>
          )}

          {/* Auto-advance countdown pill — top-right of stage */}
          {models.length > 1 && selected && (
            <div
              className="absolute top-3 right-3 px-2.5 py-1 rounded-full border border-gold/40 bg-base/70 backdrop-blur-md z-10 pointer-events-none flex items-center gap-1.5"
              data-testid="autoscroll-countdown"
            >
              {(pending || reveal || placing || revealing) ? (
                <span className="text-[9px] tracking-[0.3em] uppercase text-ink-3" data-testid="autoscroll-countdown-label">Paused</span>
              ) : (
                <>
                  <span className="text-[9px] tracking-[0.3em] uppercase text-ink-3">Auto in</span>
                  <span className="font-mono text-gold text-xs tabular-nums" data-testid="autoscroll-countdown-secs">{countdown}s</span>
                </>
              )}
            </div>
          )}

          {/* Pending-refund countdown — only surfaces when reveal errored */}
          {pending && !reveal && !revealing && !placing && (
            <div
              className="absolute top-3 left-3 px-3 py-1.5 rounded-lg border border-danger/50 bg-base/80 backdrop-blur-md z-10"
              data-testid="pending-countdown"
            >
              <div className="text-[9px] tracking-[0.3em] uppercase text-ink-3">Round #{state?.nonce ?? 0}</div>
              <div className="font-mono text-danger text-xs" data-testid="pending-countdown-secs">
                Refund in {secsLeft ?? 60}s
              </div>
            </div>
          )}

          {/* Opening overlay (mid-flight, ~500ms) */}
          <AnimatePresence>
            {(placing || revealing) && !reveal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
                data-testid="opening-overlay"
              >
                <div className="px-4 py-2 rounded-lg bg-base/85 border border-pink/50 backdrop-blur-md shadow-glow-pink flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full border-2 border-pink border-t-transparent animate-spin" />
                  <span className="text-[11px] tracking-[0.3em] uppercase text-pink">Opening…</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Win burst */}
          <AnimatePresence>{reveal?.won && <WinBurst />}</AnimatePresence>

          {/* NOTE: the WIN/LOSE result banner used to live here (absolutely
              positioned over the model stage) but it occluded the very reveal
              detail — bra colour / tattoo — that players want to see. It has
              been relocated into the betting console `BannerSlot` (above the
              choice chips). The transient WinBurst particles above are fine —
              they don't cover the reveal. */}
        </section>

        {/* Console (right/bottom) */}
        <aside
          className="shrink-0 flex flex-col gap-2 p-3 sm:p-4 [@media(min-width:1024px)_and_(min-height:900px)]:w-[38%] [@media(min-width:1024px)_and_(min-height:900px)]:flex-1 [@media(min-width:1024px)_and_(min-height:900px)]:justify-center [@media(min-width:1024px)_and_(min-height:900px)]:gap-6 [@media(min-width:1024px)_and_(min-height:900px)]:p-6"
          data-testid="controls-panel"
        >
          {/* Thumb ribbon (44px) */}
          <div className="h-11 shrink-0" data-testid="gallery-strip">
            <div className="flex items-center gap-2 h-full overflow-x-auto scrollbar-thin px-0.5 snap-x">
              {models.map((m, i) => (
                <button
                  key={m.id}
                  onClick={() => jumpToThumb(i)}
                  disabled={!!pending || placing || revealing}
                  className={
                    "shrink-0 w-8 h-8 rounded-full overflow-hidden border-2 transition disabled:cursor-not-allowed snap-start " +
                    (i === selectedIdx
                      ? "border-pink shadow-glow-pink opacity-100 scale-110"
                      : "border-line opacity-60 hover:opacity-100")
                  }
                  data-testid={`thumb-${m.id}`}
                  aria-label={`Select ${m.id}`}
                >
                  <img src={asset(m.dressed_url)} alt={m.prompt_summary} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 [@media(min-width:1024px)_and_(min-height:900px)]:gap-4">
            {/* Bet chips + input */}
            <div className="flex items-center gap-1.5">
              {betChips.map((v) => (
                <button
                  key={v}
                  onClick={() => !pending && !placing && !revealing && setBet(v)}
                  disabled={!!pending || placing || revealing}
                  className={
                    "flex-1 h-9 text-xs font-mono border rounded-lg transition " +
                    (Number(bet) === v
                      ? "border-pink text-pink bg-pink/10"
                      : "border-line text-ink-2 hover:text-ink hover:border-ink-2")
                  }
                  data-testid={`bet-chip-${v}`}
                >
                  {formatAmount(v, activeCurrency)}
                </button>
              ))}
              <button
                onClick={() => !pending && !placing && !revealing && setBet(Math.min(maxBet, Math.floor(balance)))}
                disabled={!!pending || placing || revealing}
                className="flex-1 h-9 text-[10px] tracking-widest uppercase font-mono border rounded-lg border-gold/50 text-gold hover:bg-gold/10 transition"
                data-testid="bet-max-btn"
              >
                MAX
              </button>
              <input
                id="bet"
                type="number"
                min={minBet}
                max={maxBet}
                step="any"
                value={bet}
                disabled={!!pending || placing || revealing}
                onChange={(e) => setBet(e.target.value)}
                className="w-20 h-9 bg-base border border-line rounded-lg px-2 text-sm font-mono text-ink focus:border-pink focus:outline-none focus:shadow-glow-pink transition disabled:opacity-60"
                data-testid="bet-input"
              />
            </div>

            {/* BannerSlot — reserves a fixed strip above the choice chips.
                Three mutually-exclusive states swap content in the same slot so
                the layout stays jank-free and the result banner never overlaps
                the model reveal:
                  - idle:  "Win pays X.XX×"  +  potential-payout ($X.XX)
                  - busy:  spinner + "Placing…" / "Revealing…"
                  - result: WIN gold gradient  or  LOSS red — with a
                    correct-choice pill (color swatch for bra games, text badge
                    for text-choice games like sexy-tattoo). Applies to ALL games. */}
            <div
              className="relative min-h-[44px] sm:min-h-[48px] [@media(min-width:1024px)_and_(min-height:900px)]:min-h-[56px] rounded-lg"
              data-testid="game-banner-slot"
            >
              <AnimatePresence mode="wait">
                {reveal ? (
                  <motion.div
                    key="result"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.2 }}
                    className={
                      "absolute inset-0 flex items-center justify-between gap-2 px-3 rounded-lg border " +
                      (reveal.won
                        ? "bg-gradient-to-r from-gold/25 via-gold/15 to-pink/15 border-gold/60 shadow-glow-gold"
                        : "bg-danger/15 border-danger/60 animate-shake")
                    }
                    data-testid="result-banner"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {reveal.won
                        ? <Trophy className="w-4 h-4 shrink-0" style={{ color: "#FFCE55" }} />
                        : <Sparkles className="w-4 h-4 shrink-0" style={{ color: "#FF6B6B" }} />}
                      <div className="flex flex-col leading-tight min-w-0">
                        <span className="text-[9px] tracking-[0.3em] uppercase" style={{ color: reveal.won ? "#FFCE55" : "#FF6B6B" }}>
                          {reveal.won ? "Winner" : "Better luck"}
                        </span>
                        <span className="font-mono text-sm sm:text-base font-bold truncate" style={{ color: reveal.won ? "#FFCE55" : "#FF6B6B" }}>
                          {reveal.won ? (
                            <span data-testid="payout-line">+{formatAmount(reveal.payout, reveal.currency || activeCurrency)} · {(reveal.multiplier || state?.config?.payout_multiplier || 2.85)}×</span>
                          ) : (
                            <span data-testid="lose-line">−{formatAmount(reveal.bet_amount, reveal.currency || activeCurrency)}</span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-[9px] tracking-[0.3em] uppercase text-ink-3 hidden sm:inline">Correct</span>
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border"
                        style={{
                          borderColor: (BANNER_COLOR[reveal.correct_color] || "#888") + "80",
                          background: (BANNER_COLOR[reveal.correct_color] || "#888") + "18",
                        }}
                      >
                        {state?.config?.choice_style !== "text" && (
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: BANNER_COLOR[reveal.correct_color] || "#888" }} />
                        )}
                        <span
                          className="text-[10px] font-semibold tracking-widest uppercase whitespace-nowrap"
                          style={{ color: BANNER_COLOR[reveal.correct_color] || "#888" }}
                          data-testid="correct-color"
                        >
                          {state?.config?.choice_labels?.[reveal.correct_color]
                            || CHOICE_LABEL_FALLBACK[reveal.correct_color]
                            || reveal.correct_color}
                        </span>
                      </span>
                    </div>
                  </motion.div>
                ) : (placing || revealing) ? (
                  <motion.div
                    key="busy"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-center gap-2 rounded-lg border border-line bg-elevated/60"
                    data-testid="banner-busy"
                  >
                    <div className="w-3 h-3 rounded-full border-2 border-pink border-t-transparent animate-spin" />
                    <span className="text-[10px] tracking-[0.35em] uppercase text-ink-2">
                      {revealing ? "Revealing" : "Placing"}
                    </span>
                  </motion.div>
                ) : (
                  <motion.div
                    key="idle"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-between px-3 rounded-lg border border-line/60 bg-elevated/30"
                    data-testid="banner-idle"
                  >
                    <span className="text-[10px] tracking-widest uppercase text-ink-3">
                      Win pays {state?.config?.payout_multiplier || 2.85}×
                    </span>
                    <span className="font-mono gold-text text-sm" data-testid="potential-payout">
                      {formatAmount(Number(bet) * (state?.config?.payout_multiplier || 2.85), activeCurrency)}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Choice chips — one per game choice value.
                - color-style games (bra-3-way / bra-2-way) render circular swatches
                - text-style games (sexy-tattoo) render full-width labelled buttons */}
            {(state?.config?.choice_style === "text") ? (
              <div className="grid grid-cols-2 gap-2 sm:gap-3 [@media(min-width:1024px)_and_(min-height:900px)]:gap-4" data-testid="choice-chips-text">
                {(state?.config?.colors || []).map((key) => {
                  const label = state?.config?.choice_labels?.[key] || CHOICE_LABEL_FALLBACK[key] || key.toUpperCase();
                  const active = colorPick === key;
                  const disabled = !!pending || placing || revealing;
                  return (
                    <button
                      key={key}
                      onClick={() => {
                        if (disabled) return;
                        if (colorPick !== key) soundManager.play("chip_click");
                        setColorPick(key);
                      }}
                      disabled={disabled}
                      className={
                        "relative h-16 sm:h-20 w-full rounded-xl font-semibold text-sm sm:text-base tracking-[0.24em] transition border-2 " +
                        (active
                          ? "border-pink bg-gradient-to-br from-pink/40 to-purple/25 shadow-glow-pink scale-[1.02]"
                          : "border-line bg-elevated hover:border-pink") +
                        (disabled ? " opacity-60 cursor-not-allowed" : " cursor-pointer")
                      }
                      style={{
                        // Force explicit colors — Tailwind text-* utilities were
                        // being clobbered by an inherited body `color` rule on
                        // <button>, leaving the label invisible over the tinted
                        // active-state gradient.
                        color: active ? "#FFFFFF" : "#F4EDE0",
                        textShadow: active ? "0 1px 2px rgba(0,0,0,0.6)" : "none",
                      }}
                      data-testid={`color-btn-${key}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div
                className={
                  "grid gap-2 sm:gap-3 [@media(min-width:1024px)_and_(min-height:900px)]:gap-4 " +
                  ((state?.config?.colors?.length || 3) === 2 ? "grid-cols-2" : "grid-cols-3")
                }
                data-testid="choice-chips-color"
              >
                {(state?.config?.colors || []).map((key) => {
                  const c = COLOR_META[key] || { key, label: key.toUpperCase(), hex: "#666", ring: "#999" };
                  const active = colorPick === c.key;
                  return (
                    <button
                      key={c.key}
                      onClick={() => {
                        if (pending || placing || revealing) return;
                        if (colorPick !== c.key) soundManager.play("chip_click");
                        setColorPick(c.key);
                      }}
                      disabled={!!pending || placing || revealing}
                      className={
                        "relative aspect-square max-h-16 sm:max-h-20 mx-auto w-full rounded-full font-semibold text-[10px] sm:text-xs tracking-[0.18em] uppercase transition " +
                        (active ? "scale-105" : "hover:scale-[1.03]") +
                        (pending || placing || revealing ? " opacity-60 cursor-not-allowed" : " cursor-pointer")
                      }
                      style={{
                        background: c.hex,
                        color: c.key === "gold" ? "#1a1512" : "#f4ede0",
                        boxShadow: active
                          ? `0 0 0 2px var(--bg-base), 0 0 0 4px ${c.ring}, 0 8px 24px -6px ${c.ring}`
                          : "0 3px 10px rgba(0,0,0,0.35)",
                      }}
                      data-testid={`color-btn-${c.key}`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            )}

            {error && (
              <div className="text-[11px] text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-1.5" data-testid="game-error">
                {error}
              </div>
            )}

            {/* CTA */}
            {!pending ? (
              <button
                onClick={openDress}
                disabled={!canOpen}
                className={
                  "h-12 rounded-lg font-semibold tracking-[0.12em] uppercase text-white transition " +
                  "bg-brand-gradient shadow-glow-pink " +
                  "hover:brightness-110 hover:scale-[1.01] active:scale-[0.99] " +
                  "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:hover:scale-100 " +
                  "flex items-center justify-center gap-2 relative overflow-hidden"
                }
                data-testid="place-bet-btn"
              >
                {(placing || revealing) ? (
                  <>
                    <div className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    <span>Revealing…</span>
                  </>
                ) : (
                  <span>{CTA_VERB_BY_SLUG[slug] || DEFAULT_CTA_VERB} · {formatAmount(Number(bet), activeCurrency)}</span>
                )}
              </button>
            ) : (
              <button
                onClick={retryReveal}
                disabled={revealing || secsLeft === 0}
                className={
                  "h-12 rounded-lg font-semibold tracking-[0.12em] uppercase text-[#1a1512] transition " +
                  "bg-gold-gradient shadow-glow-gold " +
                  "hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed " +
                  "flex items-center justify-center gap-2"
                }
                data-testid="reveal-btn"
              >
                {revealing ? "Opening…" : "Retry reveal"}
              </button>
            )}
          </div>

          {/* PF pill (32px) — pinned to bottom via mt-auto on tall desktops */}
          <button
            onClick={() => setShowFair(true)}
            className="h-7 shrink-0 mt-auto [@media(min-width:1024px)_and_(min-height:900px)]:mt-4 flex items-center justify-center gap-2 rounded-full border border-line hover:border-gold text-[10px] tracking-[0.3em] uppercase text-ink-3 hover:text-gold transition"
            data-testid="fair-toggle-btn"
          >
            <Lock className="w-3 h-3" />
            <span>Provably fair · nonce {state?.nonce ?? 0}</span>
          </button>
        </aside>
      </div>

      {/* ---------- Modals & drawers ---------- */}
      {showFair && state && (
        <FairDrawer
          state={state}
          pending={pending}
          onClose={() => setShowFair(false)}
          onRotate={() => { setShowFair(false); setShowRotate(true); }}
        />
      )}
      {showHistory && (
        <HistoryModal slug={slug} onClose={() => setShowHistory(false)} />
      )}
      {showTopup && (
        <TopupModal
          currency={activeCurrency}
          onClose={() => setShowTopup(false)}
          onSuccess={() => { refresh(); }}
        />
      )}
      {showRotate && state && (
        <RotateModal
          slug={slug}
          currentClient={state.client_seed}
          onClose={() => setShowRotate(false)}
          onDone={(res) => {
            // Sync the new active seed to /state — but do NOT close the modal.
            // The modal itself flips to its "revealed previous seed" view (copy
            // button + Verify past rounds → link) on success. Closing here would
            // unmount that view before the user ever sees the plaintext seed,
            // which is the only in-app surface for retrieving it.
            setState((s) => s ? { ...s, server_seed_hash: res.server_seed_hash, client_seed: res.client_seed, nonce: res.nonce } : s);
          }}
        />
      )}
      {showSoundSettings && (
        <SoundSettingsModal
          state={soundState}
          onClose={() => setShowSoundSettings(false)}
        />
      )}
      <ChatWidget />
    </div>
  );
}

// -----------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------
function WinBurst() {
  const particles = useMemo(() =>
    Array.from({ length: 28 }, (_, i) => {
      const angle = (i / 28) * Math.PI * 2 + Math.random() * 0.3;
      const dist = 120 + Math.random() * 100;
      return {
        id: i,
        x: Math.cos(angle) * dist,
        y: Math.sin(angle) * dist - 20,
        rotate: Math.random() * 720 - 360,
        size: 5 + Math.random() * 9,
        gold: Math.random() > 0.4,
        delay: Math.random() * 0.15,
      };
    }),
    []
  );
  return (
    <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-20 overflow-hidden" data-testid="win-burst">
      <motion.div
        initial={{ scale: 0, opacity: 0.9 }}
        animate={{ scale: 3, opacity: 0 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="w-24 h-24 rounded-full"
        style={{ background: "radial-gradient(circle, rgba(255,201,71,0.7) 0%, transparent 65%)" }}
      />
      {particles.map((p) => (
        <motion.span
          key={p.id}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1, rotate: 0 }}
          animate={{ x: p.x, y: p.y, opacity: 0, scale: 0.5, rotate: p.rotate }}
          transition={{ duration: 1.3, delay: p.delay, ease: "easeOut" }}
          className="absolute rounded-sm"
          style={{
            width: p.size,
            height: p.size * 0.4,
            background: p.gold
              ? "linear-gradient(135deg, #FFC947 0%, #FF9A2E 100%)"
              : "linear-gradient(135deg, #FF3B7A 0%, #8B5CF6 100%)",
            boxShadow: p.gold ? "0 0 6px rgba(255,201,71,0.6)" : "0 0 6px rgba(255,59,122,0.6)",
          }}
        />
      ))}
    </div>
  );
}

// Bottom-sheet on mobile, side-sheet on desktop
function FairDrawer({ state, pending, onClose, onRotate }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="fair-panel"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-line bg-elevated p-5 max-h-[80vh] overflow-y-auto"
        data-testid="fair-details"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lock className="w-4 h-4 text-gold" />
            <div className="font-display italic text-xl text-ink">Provably fair</div>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2.5 text-xs">
          <FairRow label="Server seed hash" value={state.server_seed_hash} testid="fair-server-hash" />
          <FairRow label="Client seed" value={state.client_seed} testid="fair-client-seed" />
          <FairRow label="Nonce" value={String(state.nonce)} testid="fair-nonce" />
        </div>
        <p className="text-[11px] text-ink-2 leading-relaxed mt-4">
          Every round is decided by an HMAC of the server seed (committed) and your client seed + nonce.
          Verify past rounds after rotating seeds — the previous server seed becomes public.
        </p>
        <div className="flex gap-2 mt-5">
          <button
            onClick={onRotate}
            disabled={!!pending}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-line hover:border-gold hover:text-gold text-[11px] tracking-[0.25em] uppercase disabled:opacity-50 disabled:cursor-not-allowed transition"
            data-testid="rotate-btn"
          >
            <RefreshCw className="w-3 h-3" /> Rotate seeds
          </button>
          <Link
            to="/verify"
            onClick={onClose}
            className="flex-1 flex items-center justify-center py-2.5 rounded-lg border border-line hover:border-pink hover:text-pink text-[11px] tracking-[0.25em] uppercase transition"
            data-testid="fair-open-verify-link"
          >
            Verifier →
          </Link>
        </div>
        {pending && (
          <div className="text-[10px] tracking-widest text-ink-3 pt-2 text-center">
            Rotation disabled while a round is pending.
          </div>
        )}
      </motion.div>
    </div>
  );
}

function HistoryModal({ slug, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/games/${slug}/history?limit=10`).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, [slug]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="history-modal"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-line bg-elevated p-5 max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="font-display italic text-xl text-ink">Recent rounds</div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        {items === null ? (
          <div className="text-xs text-ink-2 italic">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-ink-2 italic">No rounds yet.</div>
        ) : (
          <ul className="space-y-1.5 text-xs">
            {items.map((r) => (
              <li key={r.round_id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-highest/40 border border-line">
                <span className="uppercase tracking-widest text-[10px]" style={{ color: BANNER_COLOR[r.color_pick] || "#888" }}>
                  {CHOICE_LABEL_FALLBACK[r.color_pick] || r.color_pick}
                </span>
                <span className="text-ink-3 text-[10px]">→</span>
                <span className="uppercase tracking-widest text-[10px]" style={{ color: BANNER_COLOR[r.correct_color] || "#888" }}>
                  {r.correct_color ? (CHOICE_LABEL_FALLBACK[r.correct_color] || r.correct_color) : "—"}
                </span>
                <span className="text-ink-3 font-mono flex-1 text-right">
                  {r.won
                    ? <span className="text-success">+{formatAmount(r.payout, r.currency || "USDT")}</span>
                    : r.status === "refunded"
                    ? <span className="text-ink-3">refund</span>
                    : <span className="text-danger">−{formatAmount(r.bet_amount, r.currency || "USDT")}</span>}
                </span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/history" onClick={onClose} className="mt-4 block text-center text-[10px] tracking-widest uppercase text-pink hover:underline">
          Full history →
        </Link>
      </motion.div>
    </div>
  );
}

function FairRow({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const short = value.length > 26 ? `${value.slice(0, 12)}…${value.slice(-6)}` : value;
  return (
    <div className="flex justify-between items-center gap-2">
      <span className="text-[10px] tracking-[0.3em] uppercase text-ink-3">{label}</span>
      <button
        onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1000); }}
        title={value}
        className="text-ink hover:text-gold text-right whitespace-nowrap transition font-mono text-[11px]"
        data-testid={testid}
      >
        {copied ? "copied" : short}
      </button>
    </div>
  );
}

function TopupModal({ currency = "USDT", onClose, onSuccess }) {
  // Default top-up amount scales with the currency — 5 USDT vs 500 BDT.
  const defaultAmount = currency === "USDT" ? 5 : 500;
  const [amount, setAmount] = useState(defaultAmount);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/wallet/demo-topup", {
        amount: Number(amount),
        currency,
      });
      toast.success(`+${formatAmount(data.amount_added, currency)} demo credit`);
      onSuccess(data.balance_after);
      onClose();
    } catch (e2) { setErr(extractError(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }} data-testid="topup-modal">
      <div className="w-full max-w-sm rounded-2xl border border-line bg-elevated p-6">
        <form onSubmit={submit}>
          <div className="flex justify-between items-center mb-4">
            <div className="font-display italic text-2xl gold-text">Demo top-up · {currency}</div>
            <button type="button" onClick={onClose} data-testid="topup-close-btn" className="text-ink-3 hover:text-ink"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-ink-2 mb-4">
            Add up to {formatAmount(1000, currency)} in demo credits (one request per minute).
          </p>
          <Input label={`Amount (${currency})`} type="number" min="1" max="1000" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="topup-amount" inputClassName="font-mono" className="mb-4" />
          {err && <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2 mb-3" data-testid="topup-error">{err}</div>}
          <Button type="submit" disabled={busy} loading={busy} variant="primary" size="lg" className="w-full" data-testid="topup-submit-btn">{busy ? "Topping up…" : "Add credit"}</Button>
        </form>
      </div>
    </div>
  );
}

function RotateModal({ slug, currentClient, onClose, onDone }) {
  const [clientSeed, setClientSeed] = useState(currentClient);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [revealed, setRevealed] = useState(null); // { previous_server_seed, previous_server_seed_hash, new_server_seed_hash, new_client_seed, nonce_reset_to }
  const [copied, setCopied] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/games/${slug}/rotate-seed`, { new_client_seed: clientSeed || null });
      toast.success("Seeds rotated. Previous server seed revealed below.");
      // Propagate the *new* seed state to the parent so /state stays in sync.
      onDone(data);
      // Retain the revealed plaintext locally to show it to the user.
      setRevealed(data);
    } catch (e2) { setErr(extractError(e2)); } finally { setBusy(false); }
  };

  const copySeed = async () => {
    try {
      await navigator.clipboard.writeText(revealed?.previous_server_seed || revealed?.revealed_server_seed || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard is a permissioned API — blocked in iframes without
      // clipboard-write allow, private browsing on Safari, and older mobile
      // browsers. We surface a debug log but don't toast (the user still has
      // the seed visible on-screen and can copy manually).
      console.debug("[Game.copySeed] clipboard blocked:", e);
    }
  };

  const revealedSeed = revealed?.previous_server_seed || revealed?.revealed_server_seed;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }} data-testid="rotate-modal">
      <div className="w-full max-w-md rounded-2xl border border-line bg-elevated p-6">
        {revealed && revealedSeed ? (
          <div data-testid="rotate-revealed">
            <div className="flex justify-between items-center mb-4">
              <div className="font-display italic text-2xl gold-text">Previous server seed</div>
              <button type="button" onClick={onClose} data-testid="rotate-close-btn" className="text-ink-3 hover:text-ink"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-ink-2 mb-3 leading-relaxed">
              Save this plaintext seed <span className="text-gold">now</span> — it&apos;s the only way to verify past rounds under
              the seed you just retired. New rounds use a fresh seed you can&apos;t predict.
            </p>
            <div className="font-mono text-[11px] break-all bg-base border border-line rounded-lg px-3 py-3 text-ink" data-testid="rotate-revealed-seed">
              {revealedSeed}
            </div>
            <div className="mt-2 text-[10px] tracking-widest uppercase text-ink-3">
              hash: <span className="font-mono text-ink-2 break-all">{revealed?.previous_server_seed_hash}</span>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={copySeed}
                className="h-10 rounded-lg border border-line text-xs tracking-widest uppercase text-ink-2 hover:text-ink hover:border-ink-2 transition"
                data-testid="rotate-copy-seed"
              >
                {copied ? "Copied" : "Copy seed"}
              </button>
              <Link
                to={`/verify?server_seed=${encodeURIComponent(revealedSeed)}&client_seed=${encodeURIComponent(revealed?.new_client_seed || "")}&nonce=0&game_slug=${encodeURIComponent(slug)}`}
                className="h-10 flex items-center justify-center rounded-lg bg-brand-gradient text-white text-xs tracking-widest uppercase shadow-glow-pink hover:brightness-110 transition"
                data-testid="rotate-verify-link"
              >
                Verify past rounds →
              </Link>
            </div>
            <div className="mt-4 text-[10px] text-ink-3 leading-relaxed">
              New seed hash <span className="font-mono text-ink-2">{revealed?.new_server_seed_hash?.slice(0, 24)}…</span> ·
              nonce reset to <span className="text-ink-2">{revealed?.nonce_reset_to ?? 0}</span>
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="flex justify-between items-center mb-4">
              <div className="font-display italic text-2xl gold-text">Rotate seeds</div>
              <button type="button" onClick={onClose} data-testid="rotate-close-btn" className="text-ink-3 hover:text-ink"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-ink-2 mb-4">
              The active server seed is intentionally hidden while rounds are in play.
              Rotating releases the plaintext so you can verify every past round under it,
              and starts a fresh seed with a new hash.
            </p>
            <Input label="Client seed" type="text" maxLength="64" value={clientSeed} onChange={(e) => setClientSeed(e.target.value)} data-testid="rotate-client-seed" inputClassName="font-mono text-xs" hint="Leave as-is or type your own. Max 64 chars." className="mb-4" />
            {err && <div className="text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2 mb-3" data-testid="rotate-error">{err}</div>}
            <Button type="submit" disabled={busy} loading={busy} variant="primary" size="lg" className="w-full" data-testid="rotate-submit-btn">{busy ? "Rotating…" : "Rotate now"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}


function SoundSettingsModal({ state, onClose }) {
  const percent = (v) => Math.round(v * 100);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="sound-settings-modal"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl border border-line bg-elevated p-5 max-h-[85vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sliders className="w-4 h-4 text-gold" />
            <div className="font-display italic text-xl text-ink">Sound settings</div>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink" aria-label="Close" data-testid="sound-settings-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <SoundSlider
            label="Master volume"
            value={state.master}
            display={`${percent(state.master)}%`}
            onChange={(v) => soundManager.setMasterVolume(v)}
            testid="sound-master-slider"
          />
          <SoundSlider
            label="Music volume"
            value={state.music}
            display={`${percent(state.music)}%`}
            onChange={(v) => soundManager.setMusicVolume(v)}
            testid="sound-music-slider"
          />
          <SoundSlider
            label="SFX volume"
            value={state.sfx}
            display={`${percent(state.sfx)}%`}
            onChange={(v) => soundManager.setSfxVolume(v)}
            testid="sound-sfx-slider"
          />
          <SoundToggle
            label="Ambient music"
            hint="Subtle casino lounge loop while you play."
            enabled={state.musicEnabled}
            onChange={(v) => soundManager.setMusicEnabled(v)}
            testid="sound-music-toggle"
          />
          <SoundToggle
            label="Model change sound"
            hint="Soft whoosh when the model gallery auto-rotates."
            enabled={state.modelSwooshEnabled}
            onChange={(v) => soundManager.setModelSwooshEnabled(v)}
            testid="sound-swoosh-toggle"
          />
        </div>

        <div className="mt-6 flex items-center gap-2">
          <button
            onClick={() => soundManager.reset()}
            className="flex-1 h-10 rounded-lg border border-line text-[11px] tracking-[0.25em] uppercase text-ink-2 hover:text-ink hover:border-ink-2 transition"
            data-testid="sound-reset-btn"
          >
            Reset to defaults
          </button>
          <button
            onClick={() => soundManager.toggleMute()}
            className={
              "flex-1 h-10 rounded-lg text-[11px] tracking-[0.25em] uppercase transition " +
              (state.muted
                ? "bg-danger/20 border border-danger/60 text-danger"
                : "bg-brand-gradient text-white shadow-glow-pink hover:brightness-110")
            }
            data-testid="sound-master-mute-btn"
          >
            {state.muted ? "Muted · tap to unmute" : "Mute all"}
          </button>
        </div>

        <p className="mt-3 text-[10px] text-ink-3 leading-relaxed">
          Some browsers block audio until you interact with the page. If you don't hear anything,
          click anywhere first — audio will unlock automatically.
        </p>
      </motion.div>
    </div>
  );
}

function SoundSlider({ label, value, display, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] tracking-[0.3em] uppercase text-ink-3">{label}</span>
        <span className="font-mono text-xs text-ink" data-testid={`${testid}-value`}>{display}</span>
      </div>
      <input
        type="range"
        min="0"
        max="100"
        step="1"
        value={Math.round(value * 100)}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
        className="w-full accent-pink cursor-pointer"
        data-testid={testid}
      />
    </div>
  );
}

function SoundToggle({ label, hint, enabled, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink">{label}</div>
        {hint && <div className="text-[11px] text-ink-3 leading-snug">{hint}</div>}
      </div>
      <button
        onClick={() => onChange(!enabled)}
        role="switch"
        aria-checked={enabled}
        className={
          "relative w-10 h-6 rounded-full transition shrink-0 " +
          (enabled ? "bg-brand-gradient shadow-glow-pink" : "bg-highest border border-line")
        }
        data-testid={testid}
      >
        <span
          className={
            "absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all " +
            (enabled ? "left-[18px]" : "left-0.5")
          }
        />
      </button>
    </div>
  );
}
